"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { CtaButton } from "@/components/ui/CtaButton";
import { site } from "@/lib/site";

// Bottom-pinned claim prompt. Copy is passed in from the server page so the
// component stays free of next-intl on the client.
//
// It appears only after the reader has scrolled past the hero, for two reasons:
// a bar that covers content before anyone has read a word is the worst version
// of this pattern, and OneTrust pins its consent banner to the same edge on
// first paint. By the time this shows, that banner is normally gone.
const DISMISS_KEY = "tvt:sticky-claim-dismissed";
const SHOW_AFTER_PX = 700;

export function StickyClaimCta({
  message,
  button,
  closeLabel,
  linkId = "sticky_claim_cta",
}: {
  message: string;
  button: string;
  closeLabel: string;
  linkId?: string;
}) {
  // Both start false so the server and the first client render agree. Neither is
  // set synchronously inside the effect: `visible` is driven by the scroll
  // handler and `dismissed` only by the close button.
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Storage throws outright in some contexts (private windows, blocked site
    // data), so a failed read must leave the bar available rather than kill it.
    let alreadyDismissed = false;
    try {
      alreadyDismissed = window.localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      alreadyDismissed = false;
    }
    if (alreadyDismissed) return;

    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX);
    window.addEventListener("scroll", onScroll, { passive: true });
    // Deferred so the first evaluation is not a synchronous setState during the
    // effect, which cascades a second render before paint.
    const frame = window.requestAnimationFrame(onScroll);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // A viewer who cannot persist the dismissal still gets it for this page.
    }
  };

  if (dismissed) return null;

  return (
    <div
      // aria-hidden while off-screen keeps a screen reader from announcing a
      // button that is not yet reachable; the container itself never unmounts,
      // so the slide-in has something to animate.
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] transition-all duration-300 motion-reduce:transition-none ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
    >
      <div className="relative mx-auto flex max-w-3xl flex-col gap-4 rounded-[1.25rem] bg-brand p-4 shadow-[0_18px_50px_-12px_rgba(13,48,117,0.45)] sm:flex-row sm:items-center sm:gap-5 sm:p-5">
        <p className="pr-8 text-[15px] font-semibold leading-snug text-white sm:pr-0">{message}</p>
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          <CtaButton
            href={site.loginUrl}
            linkId={linkId}
            size="md"
            variant="white"
            className="w-full sm:w-auto"
          >
            {button}
          </CtaButton>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={closeLabel}
          className="absolute right-5 top-5 grid size-8 place-items-center rounded-full text-white/75 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:static sm:size-9"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
